import { Box, SxProps, Tab, Tabs, Typography, useTheme } from '@mui/material';
import { useState } from 'react';
import { useAppDispatch } from '../../../../app/hooks';
import { useTranslation } from '../../../../services/translation/hooks/useTranslation';
import { flexCenter } from '../../../commonUI/cssClasses';
import { MapDrawIDEnum, setCurrMapDrawID } from '../../onboardingSlice';
import { useGetCurrOnboardBlock } from '../hooks/useGetCurrOnboardBlock';
import BlockBoundaryFormTab from './BlockBoundaryFormTab';
import MapOnboarding from './MapOnboarding';
import OrientationFormTab from './OrientationFormTab';
const tabLabelStyles: SxProps = {
  fontSize: '1rem',
};

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <Box
      role='tabpanel'
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ width: '100%', height: '100%', pt: 2, pb: 2 }}>
          {children}
        </Box>
      )}
    </Box>
  );
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
}

const BlockBoundaryForm: React.FC = () => {
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const { t } = useTranslation();
  const [tabIndex, setTabIndex] = useState(0);
  const { currentData: block } = useGetCurrOnboardBlock();

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabIndex(newValue);
    // Set the currMapDrawID in redux to the appropriate value for the current tab
    if (newValue === 0) {
      dispatch(setCurrMapDrawID(MapDrawIDEnum.blockBoundary));
    } else if (newValue === 1) {
      dispatch(setCurrMapDrawID(MapDrawIDEnum.xNumberOrientation));
    }
  };

  const BlockNameContent = () => {
    return (
      <Box
        sx={{
          position: 'absolute',
          top: 12,
          left: 12,
          display: 'flex',
          flexDirection: 'row',
          borderBottom: `1px solid ${theme.colors.darkTheme.grey.muted}`,
        }}
      >
        <Typography
          variant='h6'
          noWrap
          sx={{
            color: theme.colors.common.white,
          }}
        >
          {`${t('Block', { ns: 'common' })}:`}
        </Typography>
        <Typography
          variant='h6'
          noWrap
          sx={{
            ml: '0.5rem',
            color: !block
              ? theme.colors.status.red.main
              : theme.colors.common.bloomfieldGreen,
          }}
        >
          {block ? `${block?.name}` : 'Select a block'}
        </Typography>
      </Box>
    );
  };

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        ...flexCenter,
      }}
    >
      <Box
        sx={{
          width: '100%',
          height: '100%',
          ...flexCenter,
          flexDirection: 'column',
          position: 'relative',
        }}
      >
        <BlockNameContent />
        <Box sx={{ width: '100%' }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs
              value={tabIndex}
              onChange={handleTabChange}
              aria-label='tabs'
              centered
              textColor='secondary'
              indicatorColor='secondary'
            >
              <Tab
                label='Boundary'
                {...a11yProps(0)}
                sx={{ ...tabLabelStyles }}
              />
              <Tab
                label='Orientation'
                {...a11yProps(1)}
                sx={{ ...tabLabelStyles }}
              />
            </Tabs>
          </Box>
          <CustomTabPanel value={tabIndex} index={0}>
            <BlockBoundaryFormTab />
          </CustomTabPanel>
          <CustomTabPanel value={tabIndex} index={1}>
            <OrientationFormTab />
          </CustomTabPanel>
        </Box>
        <Box
          sx={{
            width: '100%',
            height: '50vh',
            borderRadius: theme.borderRadius.primary,
            overflow: 'hidden',
          }}
        >
          <MapOnboarding
            showDrawControls
            showAddressSearch
            onboardingTabIndex={tabIndex}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default BlockBoundaryForm;
